// The interface's voice: synthesized UI feedback (docs/DESIGN.md audio feedback).
// Standalone WebAudio (never the music engine): UI sounds must not touch the
// transport, and must work before the studio loads. Sanctioned moments only.

const KEY = "bd.uisound";
let ctx: AudioContext | null = null;
let master: GainNode | null = null;

export function uiSoundEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

export function setUiSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(KEY, on ? "on" : "off");
  } catch {
    /* storage unavailable */
  }
}

function ensure(): { ctx: AudioContext; master: GainNode } | null {
  if (!uiSoundEnabled()) return null;
  if (!ctx) {
    try {
      ctx = new AudioContext();
      master = ctx.createGain();
      master.gain.value = 0.12; // always quiet
      master.connect(ctx.destination);
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return master ? { ctx, master } : null;
}

function tone(freq: number, dur: number, type: OscillatorType, gain: number, delay = 0): void {
  const a = ensure();
  if (!a) return;
  const t0 = a.ctx.currentTime + delay;
  const osc = a.ctx.createOscillator();
  const g = a.ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(a.master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Save: a soft major-seventh chord, barely there. */
export function soundSaved(): void {
  const root = 523.25; // C5
  [1, 5 / 4, 3 / 2, 15 / 8].forEach((ratio, i) => tone(root * ratio, 0.5, "sine", 0.5 - i * 0.08, i * 0.012));
}

/** Release / export complete: a tiny rising sparkle. */
export function soundSparkle(): void {
  [880, 1174.7, 1568, 2093].forEach((f, i) => tone(f, 0.25, "triangle", 0.4 - i * 0.06, i * 0.055));
}

/** Toggle: one soft tick. */
export function soundTick(): void {
  tone(1900, 0.03, "triangle", 0.35);
}

/** Selection: minimal confirmation. */
export function soundSelect(): void {
  tone(1320, 0.05, "sine", 0.22);
}

/** A collaborator arrives: warm two-note swell. */
export function soundArrive(): void {
  tone(392, 0.4, "sine", 0.4);
  tone(587.3, 0.5, "sine", 0.35, 0.09);
}

/** Error: low and kind, never alarming. */
export function soundGentleError(): void {
  tone(196, 0.25, "sine", 0.4);
  tone(185, 0.3, "sine", 0.3, 0.12);
}

/** Milestone: a small ascending celebration. */
export function soundCelebrate(): void {
  [523.25, 659.25, 784, 1046.5].forEach((f, i) => tone(f, 0.3, "triangle", 0.45 - i * 0.05, i * 0.07));
}
